import React, { useEffect, useMemo, useRef, useState } from "react";
import { AutoCompleteComponent } from "@syncfusion/ej2-react-dropdowns";
import { useFormContext, useWatch } from "react-hook-form";
import { get } from "lodash";
import useUtils from "services/utils/useUtils";
import Delete from "../../../../img/icons/svg/botones/delete.png";
import "./sccs/FormInputEnfermedadesCUME.scss";

interface IInputProps {
	name: string;
	required?: string;
	disabled?: boolean;
	xl?: number;
	md?: number;
	col?: number;
	label?: string;
	codeName?: string;
	detailsName?: string;
}

interface EnfermedadCUMEOption {
	id: string;
	claseEnfermedadId: string;
	claseEnfermedad: string;
	enfermedadId: string;
	enfermedad: string;
	codigoEnfermedad: string;
}

interface ClaseEnfermedadOption {
	claseEnfermedadId: string;
	claseEnfermedad: string;
}

type SyncfusionDataSourceItem = { [key: string]: Object };

const normalizeString = (value: unknown): string => String(value ?? "").trim();

const getValueByKeys = (source: Record<string, unknown>, keys: string[]): unknown => {
	const normalized = Object.entries(source).map(([k, v]) => [
		k.replace(/[_\s]/g, "").toLowerCase(),
		v,
	] as const);

	for (const key of keys) {
		const target = key.replace(/[_\s]/g, "").toLowerCase();
		const match = normalized.find(([k]) => k === target);
		if (match) return match[1];
	}

	return undefined;
};

const extractRows = (response: unknown): Record<string, unknown>[] => {
	if (Array.isArray(response)) return response as Record<string, unknown>[];
	if (!response || typeof response !== "object") return [];

	const src = response as Record<string, unknown>;
	const candidates = [src.items, src.Items, src.result, src.Result, src.data, src.Data];

	for (const candidate of candidates) {
		if (Array.isArray(candidate)) return candidate as Record<string, unknown>[];
	}

	return [];
};

const normalizeToStringArray = (value: unknown): string[] => {
	if (Array.isArray(value)) {
		return value
			.map((item) => normalizeString(item))
			.filter((item) => item !== "");
	}

	if (value === null || value === undefined || value === "") return [];

	return [normalizeString(value)].filter((item) => item !== "");
};

const buildSelectionKey = (item: Pick<EnfermedadCUMEOption, "claseEnfermedadId" | "enfermedadId">): string => {
	return `${normalizeString(item.claseEnfermedadId)}::${normalizeString(item.enfermedadId)}`;
};

const uniqueSelections = (items: EnfermedadCUMEOption[]): EnfermedadCUMEOption[] => {
	const seen = new Set<string>();
	const result: EnfermedadCUMEOption[] = [];

	items.forEach((item) => {
		const key = buildSelectionKey(item);
		if (seen.has(key)) return;
		seen.add(key);
		result.push(item);
	});

	return result;
};

const areSelectionsEqual = (left: EnfermedadCUMEOption[], right: EnfermedadCUMEOption[]): boolean => {
	if (left.length !== right.length) return false;

	for (let index = 0; index < left.length; index += 1) {
		if (buildSelectionKey(left[index]) !== buildSelectionKey(right[index])) {
			return false;
		}
	}

	return true;
};

const areStringArraysEqual = (left: string[], right: string[]): boolean => {
	if (left.length !== right.length) return false;

	for (let index = 0; index < left.length; index += 1) {
		if (normalizeString(left[index]) !== normalizeString(right[index])) {
			return false;
		}
	}

	return true;
};

const normalizeRequiredMessage = (required?: string): string | false => {
	if (typeof required === "string" && required.trim() !== "") return required;
	return false;
};

const FormInputEnfermedadesCUME: React.FC<IInputProps> = ({
	name,
	required,
	disabled = false,
	xl = 12,
	md = 12,
	col = 12,
	label = "Enfermedades graves",
	codeName,
	detailsName,
}) => {
	const { getClaseEnfermedadesCUME } = useUtils();
	const {
		register,
		control,
		getValues,
		setValue,
		formState: { errors },
	} = useFormContext();

	const [allData, setAllData] = useState<EnfermedadCUMEOption[]>([]);
	const [catalogLoaded, setCatalogLoaded] = useState(false);

	const [selectedClaseId, setSelectedClaseId] = useState("");
	const [selectedClaseName, setSelectedClaseName] = useState("");
	const [selectedEnfermedadId, setSelectedEnfermedadId] = useState("");
	const [selectedEnfermedadName, setSelectedEnfermedadName] = useState("");

	const [selectedItems, setSelectedItems] = useState<EnfermedadCUMEOption[]>([]);
	const [selectionError, setSelectionError] = useState<string>("");
	const isHydratingSelectionRef = useRef(false);
	const claseAutoCompleteRef = useRef<AutoCompleteComponent | null>(null);
	const enfermedadAutoCompleteRef = useRef<AutoCompleteComponent | null>(null);

	const currentSelectedIds = useWatch({ control, name, defaultValue: getValues(name) });
	const currentSelectedCodes = useWatch({
		control,
		name: codeName || `${name}CodigosEnfermedadWatcher`,
		defaultValue: codeName ? getValues(codeName) : [],
	});

	const requiredMessage = normalizeRequiredMessage(required);

	const claseOptions = useMemo(() => {
		const map = new Map<string, string>();

		allData.forEach((item) => {
			const id = normalizeString(item.claseEnfermedadId);
			const description = normalizeString(item.claseEnfermedad);

			if (!id) return;
			if (!map.has(id)) {
				map.set(id, description || id);
			}
		});

		return Array.from(map.entries())
			.map(([claseEnfermedadId, claseEnfermedad]) => ({ claseEnfermedadId, claseEnfermedad }))
			.sort((a, b) => a.claseEnfermedad.localeCompare(b.claseEnfermedad, "es", { sensitivity: "base" }));
	}, [allData]);

	const enfermedadOptions = useMemo(() => {
		if (!selectedClaseId) return [];

		const filteredByClass = allData.filter(
			(item) => normalizeString(item.claseEnfermedadId) === normalizeString(selectedClaseId)
		);

		const uniqueByDisease = new Map<string, EnfermedadCUMEOption>();
		filteredByClass.forEach((item) => {
			const diseaseId = normalizeString(item.enfermedadId);
			if (!diseaseId) return;
			if (!uniqueByDisease.has(diseaseId)) {
				uniqueByDisease.set(diseaseId, item);
			}
		});

		return Array.from(uniqueByDisease.values()).sort((a, b) =>
			a.enfermedad.localeCompare(b.enfermedad, "es", { sensitivity: "base" })
		);
	}, [allData, selectedClaseId]);

	useEffect(() => {
		let isMounted = true;

		const fetchData = async () => {
			const res = await getClaseEnfermedadesCUME();
			const rows = extractRows(res);

			const mappedData = rows
				.map((row) => {
					const rawId = getValueByKeys(row, ["id", "Id"]);
					const rawClaseId = getValueByKeys(row, [
						"claseEnfermedadId",
						"ClaseEnfermedadId",
						"clase_enfermedad_id",
						"claseId",
						"ClaseId",
					]);
					const rawClase = getValueByKeys(row, [
						"claseEnfermedad",
						"ClaseEnfermedad",
						"clase_enfermedad",
						"clase",
						"Clase",
					]);
					const rawEnfermedadId = getValueByKeys(row, ["enfermedadId", "EnfermedadId", "enfermedad_id"]);
					const rawEnfermedad = getValueByKeys(row, [
						"enfermedad",
						"Enfermedad",
						"descripcion",
						"Descripcion",
						"description",
					]);
					const rawCodigoEnfermedad = getValueByKeys(row, [
						"codigoEnfermedad",
						"CodigoEnfermedad",
						"codigo_enfermedad",
						"codigo",
						"Codigo",
					]);

					return {
						id: normalizeString(rawId),
						claseEnfermedadId: normalizeString(rawClaseId),
						claseEnfermedad: normalizeString(rawClase),
						enfermedadId: normalizeString(rawEnfermedadId),
						enfermedad: normalizeString(rawEnfermedad),
						codigoEnfermedad: normalizeString(rawCodigoEnfermedad),
					};
				})
				.filter((item) => item.claseEnfermedadId !== "" && item.enfermedadId !== "" && item.enfermedad !== "");

			const deduped = uniqueSelections(mappedData);

			if (isMounted) {
				setAllData(deduped);
				setCatalogLoaded(true);
			}
		};

		fetchData();

		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		if (!catalogLoaded) return;

		const idsFromForm = normalizeToStringArray(currentSelectedIds ?? getValues(name));
		const codesFromForm = codeName
			? normalizeToStringArray(currentSelectedCodes ?? getValues(codeName))
			: [];

		if (!idsFromForm.length && !codesFromForm.length) {
			if (selectedItems.length) {
				isHydratingSelectionRef.current = true;
				setSelectedItems([]);
			}
			return;
		}

		const hydratedByIds = idsFromForm
			.map((id) =>
				allData.find((item) => normalizeString(item.enfermedadId) === normalizeString(id))
			)
			.filter((item): item is EnfermedadCUMEOption => Boolean(item));

		const hydratedByCodes = codesFromForm
			.map((code) =>
				allData.find((item) => normalizeString(item.codigoEnfermedad) === normalizeString(code))
			)
			.filter((item): item is EnfermedadCUMEOption => Boolean(item));

		const hydrated = uniqueSelections(
			[...hydratedByIds, ...hydratedByCodes]
		);

		if (!areSelectionsEqual(hydrated, selectedItems)) {
			isHydratingSelectionRef.current = true;
			setSelectedItems(hydrated);
		}
	}, [catalogLoaded, currentSelectedIds, currentSelectedCodes, getValues, name, codeName, allData]);

	useEffect(() => {
		if (!catalogLoaded) return;

		if (isHydratingSelectionRef.current) {
			isHydratingSelectionRef.current = false;
			return;
		}

		const enfermedadIds = selectedItems.map((item) => item.enfermedadId);
		const codigosEnfermedad = selectedItems
			.map((item) => item.codigoEnfermedad)
			.filter((item) => normalizeString(item) !== "");

		const currentEnfermedadIds = normalizeToStringArray(getValues(name));
		if (!areStringArraysEqual(currentEnfermedadIds, enfermedadIds)) {
			setValue(name, enfermedadIds, { shouldDirty: true, shouldValidate: true });
		}

		if (codeName) {
			const currentCodigos = normalizeToStringArray(getValues(codeName));
			if (!areStringArraysEqual(currentCodigos, codigosEnfermedad)) {
				setValue(codeName, codigosEnfermedad, { shouldDirty: true });
			}
		}

		if (detailsName) {
			setValue(detailsName, selectedItems, { shouldDirty: true });
		}
	}, [selectedItems, catalogLoaded, name, codeName, detailsName, setValue, getValues]);

	useEffect(() => {
		if (codeName) register(codeName);
		if (detailsName) register(detailsName);
	}, [codeName, detailsName, register]);

	const handleClaseChange = (value: unknown, itemData?: unknown) => {
		const claseName = normalizeString(value);
		const selectedOption =
			(itemData as ClaseEnfermedadOption | undefined) ||
			claseOptions.find((item) => item.claseEnfermedad === claseName);

		if (selectedOption) {
			setSelectedClaseId(selectedOption.claseEnfermedadId);
			setSelectedClaseName(selectedOption.claseEnfermedad);
		} else {
			setSelectedClaseId("");
			setSelectedClaseName(claseName);
		}

		setSelectedEnfermedadId("");
		setSelectedEnfermedadName("");
		setSelectionError("");
	};

	const handleEnfermedadChange = (value: unknown, itemData?: unknown) => {
		const enfermedadName = normalizeString(value);
		const selectedOption =
			(itemData as EnfermedadCUMEOption | undefined) ||
			enfermedadOptions.find((item) => item.enfermedad === enfermedadName);

		if (selectedOption) {
			setSelectedEnfermedadId(selectedOption.enfermedadId);
			setSelectedEnfermedadName(selectedOption.enfermedad);
		} else {
			setSelectedEnfermedadId("");
			setSelectedEnfermedadName(enfermedadName);
		}

		setSelectionError("");
	};

	const handleAddDisease = () => {
		if (disabled) return;

		if (!selectedClaseId || !selectedEnfermedadId) {
			setSelectionError("Debe seleccionar el tipo de enfermedad y la enfermedad para añadirla.");
			return;
		}

		const selected = allData.find(
			(item) =>
				normalizeString(item.claseEnfermedadId) === normalizeString(selectedClaseId) &&
				normalizeString(item.enfermedadId) === normalizeString(selectedEnfermedadId)
		);

		if (!selected) {
			setSelectionError("No se ha podido añadir la enfermedad seleccionada.");
			return;
		}

		const alreadySelected = selectedItems.some(
			(item) => buildSelectionKey(item) === buildSelectionKey(selected)
		);

		if (alreadySelected) {
			setSelectionError("La enfermedad seleccionada ya está añadida en la lista.");
			return;
		}

		setSelectedItems((prev) => [...prev, selected]);
		setSelectedEnfermedadId("");
		setSelectedEnfermedadName("");
		setSelectionError("");
	};

	const handleRemoveDisease = (itemToRemove: EnfermedadCUMEOption) => {
		setSelectedItems((prev) =>
			prev.filter((item) => buildSelectionKey(item) !== buildSelectionKey(itemToRemove))
		);
		setSelectionError("");
	};

	const openPopupOnFocus = (component: AutoCompleteComponent | null, canOpen: boolean) => {
		if (!canOpen || !component) return;
		component.showPopup();
	};

	const errorMessage = get(errors, name)?.message as string | undefined;

	return (
		<div className={`col-xl-${xl} col-md-${md} col-${col}`}>
			<fieldset
				className={`input-block input-block--code form-enfermedades-cume ${errorMessage ? "error" : ""}`}
				data-input-name={name}
			>
				<div className="container-leyend">
					<legend className="input-block--code__legend">{label}</legend>
				</div>

				<div className="row align-items-end g-2">
					<div className="col-12 col-md-5 delta-autocomplete">
						<label className="mb-1">Tipo de enfermedad</label>
						<AutoCompleteComponent
							ref={(instance) => {
								claseAutoCompleteRef.current = instance;
							}}
							id={`${name}ClaseEnfermedad`}
							dataSource={claseOptions as unknown as SyncfusionDataSourceItem[]}
							fields={{ text: "claseEnfermedad", value: "claseEnfermedad" }}
							placeholder="Seleccione el tipo de enfermedad"
							value={selectedClaseName}
							change={(e) => {
								handleClaseChange(e?.value, e?.itemData);
							}}
							enabled={!disabled}
							ignoreCase={true}
							ignoreAccent={true}
							filterType="Contains"
							minLength={0}
							focus={() => {
								openPopupOnFocus(claseAutoCompleteRef.current, !disabled);
							}}
						/>
					</div>

					<div className="col-12 col-md-5 delta-autocomplete">
						<label className="mb-1">Enfermedad</label>
						<AutoCompleteComponent
							ref={(instance) => {
								enfermedadAutoCompleteRef.current = instance;
							}}
							id={`${name}Enfermedad`}
							dataSource={enfermedadOptions as unknown as SyncfusionDataSourceItem[]}
							fields={{ text: "enfermedad", value: "enfermedad" }}
							placeholder="Seleccione la enfermedad"
							value={selectedEnfermedadName}
							change={(e) => {
								handleEnfermedadChange(e?.value, e?.itemData);
							}}
							enabled={!disabled && !!selectedClaseId}
							ignoreCase={true}
							ignoreAccent={true}
							filterType="Contains"
							minLength={0}
							focus={() => {
								openPopupOnFocus(enfermedadAutoCompleteRef.current, !disabled && !!selectedClaseId);
							}}
						/>
					</div>

					<div className="col-12 col-md-2">
						<button
							type="button"
							className="form-enfermedades-cume__add-button"
							onClick={handleAddDisease}
							disabled={disabled || !selectedClaseId || !selectedEnfermedadId}
							aria-label="Añadir enfermedad"
						>
							+
						</button>
					</div>
				</div>

				<div className="table-responsive mt-3">
					{selectedItems.length > 0 ? (
						<table className="table table-sm align-middle mb-2">
							<thead>
								<tr>
									<th scope="col">Tipo</th>
									<th scope="col">Enfermedad</th>
								
								</tr>
							</thead>
							<tbody>
								{selectedItems.map((item) => (
									<tr key={buildSelectionKey(item)}>
										<td>{item.claseEnfermedad}</td>
										<td>{item.enfermedad}</td>
										<td className="text-end">
											<button
												type="button"
												className="remove-file-btn me-2"
												aria-label={`Eliminar enfermedad ${item.enfermedad}`}
												onClick={() => { if (!disabled) handleRemoveDisease(item); }}
												disabled={disabled}
												style={{ opacity: disabled ? 0.5 : 1 }}
											>
												<img src={Delete} alt="" aria-hidden="true" style={{ width: 18, height: 18 }} />
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					) : (
						<p className="text-muted mb-2">No hay enfermedades añadidas.</p>
					)}
				</div>

				<input
					type="hidden"
					{...register(name, {
						validate: (value) => {
							if (!requiredMessage) return true;
							return normalizeToStringArray(value).length > 0 ? true : requiredMessage;
						},
					})}
				/>

				{codeName && <input type="hidden" {...register(codeName)} />}
				{detailsName && <input type="hidden" {...register(detailsName)} />}

				{selectionError && <p className="error m-0 mt-2">{selectionError}</p>}
				{errorMessage && <p className="error m-0">{errorMessage}</p>}
			</fieldset>
		</div>
	);
};

export default FormInputEnfermedadesCUME;
